<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>Consultez les entrées et sorties de caisse par gare : crédits, débits et totaux de la période filtrée.</span>
      </div>
    </div>

    <div class="ov-grid">
      <!-- ===== TABLE DES TRANSACTIONS ===== -->
      <div class="ov-stack">
        <div class="ov-card" v-if="isAgentGare || hasSearched">
          <div class="ov-card__head">
            <div class="ov-card__title">
              Transactions
              <span class="ov-count">{{ filteredTransactions.length }}</span>
              <span v-if="!isAgentGare && selectedGare" class="ov-badge ov-badge--navy ov-badge--plain">
                <v-icon x-small color="#0c2461">mdi-bus-stop</v-icon>
                {{ selectedGare }}
              </span>
            </div>
            <div class="ov-filters">
              <div class="ov-search">
                <v-icon small>mdi-magnify</v-icon>
                <input
                  v-model="search"
                  type="text"
                  class="ov-input"
                  :placeholder="$t('cashRegister.list.searchPlaceholder')"
                />
              </div>
              <select v-model="selectedSens" class="ov-select">
                <option :value="null">Tous les sens</option>
                <option value="CREDIT">Crédit</option>
                <option value="DEBIT">Débit</option>
              </select>
              <div class="tl-dates">
                <input v-model="dateDebut" type="date" class="ov-input" title="Date début" />
                <span>—</span>
                <input v-model="dateFin" type="date" class="ov-input" title="Date fin" />
              </div>
            </div>
          </div>

          <v-data-table
            :headers="headers"
            :items="filteredTransactions"
            :loading="loading"
            loader-height="2"
            :items-per-page="15"
            :footer-props="{ 'items-per-page-options': [10, 15, 25, 50] }"
            class="ov-table"
            :no-data-text="$t('cashRegister.list.noData')"
            :loading-text="$t('cashRegister.list.loadingData')"
          >
            <template v-slot:[`item.createdAt`]="{ item }">
              <span class="tl-date">{{ item.createdAt || '—' }}</span>
            </template>

            <template v-slot:[`item.referenceTransaction`]="{ item }">
              <span class="tl-ref">{{ item.referenceTransaction || '—' }}</span>
            </template>

            <template v-slot:[`item.montantTransaction`]="{ item }">
              <span class="tl-amount">
                {{ item.montantTransaction ? item.montantTransaction.toLocaleString('fr-FR') : '—' }}
                <span>FCFA</span>
              </span>
            </template>

            <template v-slot:[`item.motifTransaction`]="{ item }">
              <span class="tl-muted">{{ item.motifTransaction || '—' }}</span>
            </template>

            <template v-slot:[`item.sens`]="{ item }">
              <span
                class="ov-badge"
                :class="item.sens === 'CREDIT' ? 'ov-badge--success' : (item.sens === 'DEBIT' ? 'ov-badge--danger' : 'ov-badge--neutral')"
              >
                {{ item.sens === 'CREDIT' ? 'Crédit' : (item.sens === 'DEBIT' ? 'Débit' : (item.sens || '—')) }}
              </span>
            </template>

            <template v-slot:[`item.typeOperation`]="{ item }">
              <span class="tl-strong">{{ item.typeOperation || '—' }}</span>
            </template>

            <template v-slot:[`item.typePaiement`]="{ item }">
              <span class="tl-muted">{{ item.typePaiement || '—' }}</span>
            </template>
          </v-data-table>
        </div>

        <!-- ===== ÉTAT INITIAL (admin sans sélection) ===== -->
        <div v-if="!isAgentGare && !hasSearched && !loading" class="ov-card">
          <div class="ov-card__body">
            <div class="ov-empty">
              {{ isAdminSocieteMere ? $t('cashRegister.list.emptyStateCompanyAndStation') : $t('cashRegister.list.emptyStateStation') }}
            </div>
          </div>
        </div>
      </div>

      <!-- ===== COLONNE LATÉRALE ===== -->
      <div class="ov-sticky ov-stack">
        <!-- Filtres (rôles admin) -->
        <div v-if="!isAgentGare" class="ov-card">
          <div class="ov-card__head">
            <div class="ov-card__title">{{ $t('cashRegister.list.filterTitle') }}</div>
          </div>
          <div class="ov-card__body tl-filter-body">
            <!-- Compagnie (Société mère uniquement) -->
            <div v-if="isAdminSocieteMere" class="ov-field">
              <label class="ov-label">{{ $t('cashRegister.list.selectCompany') }}</label>
              <select
                v-model="selectedCompagnie"
                class="ov-select"
                :disabled="loadingCompagnies"
                @change="onCompagnieChange(selectedCompagnie)"
              >
                <option :value="null">{{ $t('cashRegister.list.selectCompanyPlaceholder') }}</option>
                <option
                  v-for="c in compagnies"
                  :key="c.raisonSociale"
                  :value="c.raisonSociale"
                >{{ c.raisonSociale }}</option>
              </select>
              <div v-if="loadingCompagnies" class="ov-hint">{{ $t('cashRegister.list.loadingCompanies') }}</div>
              <div v-if="!loadingCompagnies && compagnies.length === 0" class="ov-hint">{{ $t('cashRegister.list.noCompany') }}</div>
            </div>

            <!-- Gare -->
            <div v-if="isAdminCompagnie || (isAdminSocieteMere && selectedCompagnie)" class="ov-field">
              <label class="ov-label">{{ $t('cashRegister.list.selectStation') }}</label>
              <select
                v-model="selectedGare"
                class="ov-select"
                :disabled="loadingStations"
                @change="loadTransactions"
              >
                <option :value="null">{{ $t('cashRegister.list.selectStationPlaceholder') }}</option>
                <option
                  v-for="s in stations"
                  :key="s.designation"
                  :value="s.designation"
                >{{ s.designation }}</option>
              </select>
              <div v-if="loadingStations" class="ov-hint">{{ $t('cashRegister.list.loadingStations') }}</div>
              <div v-if="!loadingStations && stations.length === 0 && (isAdminCompagnie || selectedCompagnie)" class="ov-hint">{{ $t('cashRegister.list.noStation') }}</div>
            </div>
          </div>
        </div>

        <!-- Récapitulatif -->
        <div v-if="isAgentGare || hasSearched" class="ov-card">
          <div class="ov-card__head">
            <div class="ov-card__title">Récapitulatif</div>
          </div>
          <div class="ov-card__body tl-summary">
            <div class="ov-stat">
              <span class="ov-stat__label">Total Crédit</span>
              <span class="ov-stat__value ov-stat__value--accent">{{ totalCredit.toLocaleString('fr-FR') }} <small>FCFA</small></span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">Total Débit</span>
              <span class="ov-stat__value tl-debit">{{ totalDebit.toLocaleString('fr-FR') }} <small>FCFA</small></span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">Total Global</span>
              <span class="ov-stat__value">{{ totalGlobal.toLocaleString('fr-FR') }} <small>FCFA</small></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_GET_TRANSACTIONS_BY_GARE,
  API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import { showErrorMessage } from "@/modules/messages/messageProcess";
import {
  ROLE_ADMIN_SOCIETE_MERE,
  ROLE_ADMIN_COMPAGNIE_TRANSPORT,
  ROLE_AGENT_GARE,
} from "@/components/globalConfig/constUsersRoles";
import { getStationApi } from "@/modules/common/services/commonApi";

export default {
  name: "TransactionListView",
  data() {
    return {
      errorMsg: null,
      loading: false,
      search: "",
      transactions: [],
      hasSearched: false,
      
      // Filtres de date
      dateDebut: null,
      dateFin: null,

      compagnies: [],
      stations: [],
      selectedCompagnie: null,
      selectedGare: null,
      loadingCompagnies: false,
      loadingStations: false,

      dataCompagnyObject: { index: 0, size: 100 },
      dataStationObject: { data: { compagnieTransportRaisonSociale: null } },

      queryObject: { data: { gareDesignation: null } },

      selectedSens: null,

      headers: [
        { text: "DATE",             value: "createdAt",           sortable: true, align: "start"  },
        { text: "RÉFÉRENCE",        value: "referenceTransaction", sortable: true, align: "start"  },
        { text: "MONTANT",          value: "montantTransaction",   sortable: true, align: "start"  },
        { text: "MOTIF",            value: "motifTransaction",     sortable: false, align: "start" },
        { text: "SENS",             value: "sens",                 sortable: true, align: "start"  },
        { text: "OPÉRATION",        value: "typeOperation",        sortable: true, align: "start"  },
        { text: "TYPE DE PAIEMENT", value: "typePaiement",         sortable: true, align: "start"  },
      ],
    };
  },

  computed: {
    isAgentGare() {
      return this.$store.state.userAuthentified.roleCode === ROLE_AGENT_GARE;
    },
    isAdminCompagnie() {
      return this.$store.state.userAuthentified.roleCode === ROLE_ADMIN_COMPAGNIE_TRANSPORT;
    },
    isAdminSocieteMere() {
      return this.$store.state.userAuthentified.roleCode === ROLE_ADMIN_SOCIETE_MERE;
    },
    filteredTransactions() {
      let result = this.transactions;
      
      // Filtre par sens
      if (this.selectedSens) {
        result = result.filter(t => t.sens === this.selectedSens);
      }
      
      // Filtre par date
      if (this.dateDebut || this.dateFin) {
        result = result.filter(t => {
          if (!t.createdAt) return false;
          
          // Extraire la date de createdAt (format: "DD/MM/YYYY HH:mm:ss")
          const dateParts = t.createdAt.split(' ')[0].split('/');
          if (dateParts.length !== 3) return true;
          
          const transactionDate = new Date(
            parseInt(dateParts[2]), // année
            parseInt(dateParts[1]) - 1, // mois (0-indexed)
            parseInt(dateParts[0]) // jour
          );
          
          if (this.dateDebut) {
            const debut = new Date(this.dateDebut);
            debut.setHours(0, 0, 0, 0);
            if (transactionDate < debut) return false;
          }
          
          if (this.dateFin) {
            const fin = new Date(this.dateFin);
            fin.setHours(23, 59, 59, 999);
            if (transactionDate > fin) return false;
          }
          
          return true;
        });
      }
      
      // Filtre par recherche
      if (this.search) {
        const q = this.search.toLowerCase();
        result = result.filter((t) =>
          Object.values(t).some((v) => v && String(v).toLowerCase().includes(q))
        );
      }
      
      return result;
    },
    totalCredit() {
      return this.filteredTransactions
        .filter(t => t.sens === 'CREDIT')
        .reduce((sum, t) => sum + (t.montantTransaction || 0), 0);
    },
    totalDebit() {
      return this.filteredTransactions
        .filter(t => t.sens === 'DEBIT')
        .reduce((sum, t) => sum + (t.montantTransaction || 0), 0);
    },
    totalGlobal() {
      return this.filteredTransactions
        .reduce((sum, t) => sum + (t.montantTransaction || 0), 0);
    },
  },

  methods: {
    // Agent gare : chargement direct
    async getTransactions() {
      this.loading = true;
      this.queryObject.data.gareDesignation =
        this.$store.state.userAuthentified.gareDesignation;
      await axios
        .post(API_GET_TRANSACTIONS_BY_GARE, this.queryObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status === 200 && response.data.status.code == 800) {
            this.transactions = response.data.items || [];
          } else {
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e.message || "Erreur lors du chargement";
          showErrorMessage();
        })
        .finally(() => { this.loading = false; });
    },

    resetTransactions() {
      this.transactions = [];
      this.hasSearched = false;
    },

    // Admin société mère : charger les compagnies validées
    async loadCompagnies() {
      this.loadingCompagnies = true;
      await axios
        .post(API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES, this.dataCompagnyObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200 && response.data.status.code == 800) {
            this.compagnies = response.data.items || [];
          } else {
            this.compagnies = [];
          }
        })
        .catch((e) => {
          console.error(e);
          this.compagnies = [];
        })
        .finally(() => { this.loadingCompagnies = false; });
    },

    // Admin société mère : quand une compagnie est sélectionnée
    async onCompagnieChange(raisonSociale) {
      this.stations = [];
      this.selectedGare = null;
      this.transactions = [];
      this.hasSearched = false;
      if (!raisonSociale) return;
      this.dataStationObject.data.compagnieTransportRaisonSociale = raisonSociale;
      this.loadingStations = true;
      try {
        const result = await getStationApi(
          this.dataStationObject,
          this.$store.state.userAuthentified.token
        );
        this.stations = Array.isArray(result) ? result : [];
      } catch {
        this.errorMsg = "Erreur lors du chargement des gares";
        showErrorMessage();
      } finally {
        this.loadingStations = false;
      }
    },

    // Admin compagnie : charger les gares de la compagnie connectée
    async loadStationsForCompagnie() {
      this.dataStationObject.data.compagnieTransportRaisonSociale =
        this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
      this.loadingStations = true;
      try {
        this.stations = await getStationApi(
          this.dataStationObject,
          this.$store.state.userAuthentified.token
        );
      } catch {
        this.errorMsg = "Erreur lors du chargement des gares";
        showErrorMessage();
      } finally {
        this.loadingStations = false;
      }
    },

    // Admin : charger les transactions de la gare sélectionnée
    async loadTransactions() {
      if (!this.selectedGare) { this.resetTransactions(); return; }
      this.loading = true;
      this.transactions = [];
      this.hasSearched = true;
      this.queryObject.data.gareDesignation = this.selectedGare;
      await axios
        .post(API_GET_TRANSACTIONS_BY_GARE, this.queryObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status === 200 && response.data.status.code == 800) {
            this.transactions = response.data.items || [];
          } else {
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e.message || "Erreur lors du chargement";
          showErrorMessage();
        })
        .finally(() => { this.loading = false; });
    },
  },

  mounted() {
    if (this.isAgentGare) {
      this.getTransactions();
    } else if (this.isAdminCompagnie) {
      this.loadStationsForCompagnie();
    } else if (this.isAdminSocieteMere) {
      this.loadCompagnies();
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

/* Plage de dates dans l'en-tête de la carte */
.tl-dates {
  display: flex;
  align-items: center;
  gap: 6px;

  .ov-input {
    width: 150px;
    height: 40px;
    padding: 0 10px;
    font-size: 13px;
  }

  span {
    color: $ov-subtle;
    font-weight: 700;
  }
}

.tl-filter-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.tl-summary {
  padding-top: 8px;
  padding-bottom: 8px;

  small {
    font-size: 11px;
    font-weight: 700;
    color: $ov-subtle;
  }
}

.tl-debit {
  color: $ov-danger;
}

/* Cellules */
.tl-date {
  font-size: 13px;
  color: $ov-muted;
  white-space: nowrap;
}

.tl-ref {
  font-weight: 800;
  color: $ov-navy;
}

.tl-amount {
  font-weight: 800;
  color: $ov-navy;
  white-space: nowrap;

  span {
    margin-left: 2px;
    font-size: 11px;
    font-weight: 700;
    color: $ov-subtle;
  }
}

.tl-muted {
  color: $ov-muted;
}

.tl-strong {
  font-weight: 700;
}

@media (max-width: 760px) {
  .tl-dates {
    width: 100%;

    .ov-input {
      flex: 1;
      width: auto;
      min-width: 0;
    }
  }
}
</style>
